import type { VpCamera, VpClippingPlane, VpVec3 } from '@viewer/lib/viewpoint'
import {
  direccaoDeIfc,
  pontoDeIfc,
  type Referencial,
} from './bcfCoordenadas'

/**
 * Leitura de um `.bcfv` (VisualizationInfo, BCF 2.1 e 3.0).
 *
 * Devolve a câmara e os planos já em coordenadas da cena, com a
 * conversão da fronteira (`bcfCoordenadas.ts`). Nunca lança: um `.bcfv`
 * ilegível dá null, e a importação continua com o resto do tópico.
 */

export interface VisualizacaoLida {
  camera: VpCamera | null
  clipping_planes: VpClippingPlane[]
  selected_global_ids: string[]
  hidden_global_ids: string[]
  visible_global_ids: string[]
  default_visibility: boolean
}

function filho(el: Element | null, tag: string): Element | null {
  if (!el) return null
  for (const c of Array.from(el.children)) if (c.tagName === tag) return c
  return null
}

function numero(el: Element | null, tag: string): number | null {
  const t = filho(el, tag)?.textContent
  if (t == null || t.trim() === '') return null
  const n = Number(t)
  return Number.isFinite(n) ? n : null
}

function vector(el: Element | null): VpVec3 | null {
  if (!el) return null
  const x = numero(el, 'X')
  const y = numero(el, 'Y')
  const z = numero(el, 'Z')
  return x === null || y === null || z === null ? null : { x, y, z }
}

function guids(el: Element | null): string[] {
  if (!el) return []
  const out: string[] = []
  for (const c of Array.from(el.children)) {
    if (c.tagName !== 'Component') continue
    const g = c.getAttribute('IfcGuid')
    if (g) out.push(g)
  }
  return [...new Set(out)]
}

function lerCamara(raiz: Element, ref: Referencial | null | undefined): VpCamera | null {
  const persp = filho(raiz, 'PerspectiveCamera')
  const orto = filho(raiz, 'OrthogonalCamera')
  const el = persp ?? orto
  if (!el) return null
  const pos = vector(filho(el, 'CameraViewPoint'))
  const dir = vector(filho(el, 'CameraDirection'))
  const up = vector(filho(el, 'CameraUpVector'))
  if (!pos || !dir || !up) return null
  const camera: VpCamera = {
    mode: persp ? 'perspective' : 'orthographic',
    position: pontoDeIfc(pos, ref),
    direction: direccaoDeIfc(dir, ref),
    up: direccaoDeIfc(up, ref),
  }
  const fov = numero(el, 'FieldOfView')
  if (fov !== null) camera.field_of_view = fov
  const escala = numero(el, 'ViewToWorldScale')
  if (escala !== null) camera.view_to_world_scale = escala
  const aspecto = numero(el, 'AspectRatio')
  if (aspecto !== null) camera.aspect_ratio = aspecto
  return camera
}

export function lerVisualizacao(
  xml: string,
  ref?: Referencial | null,
): VisualizacaoLida | null {
  try {
    const doc = new DOMParser().parseFromString(xml, 'application/xml')
    if (doc.getElementsByTagName('parsererror')[0]) return null
    const raiz = doc.documentElement
    if (!raiz || raiz.tagName !== 'VisualizationInfo') return null

    const comp = filho(raiz, 'Components')
    const vis = filho(comp, 'Visibility')
    // No esquema, DefaultVisibility vale false quando não vem escrito.
    const porOmissao = vis?.getAttribute('DefaultVisibility') === 'true'
    const excepcoes = guids(filho(vis, 'Exceptions'))

    const planos: VpClippingPlane[] = []
    for (const p of Array.from(filho(raiz, 'ClippingPlanes')?.children ?? [])) {
      if (p.tagName !== 'ClippingPlane') continue
      const loc = vector(filho(p, 'Location'))
      const dir = vector(filho(p, 'Direction'))
      if (!loc || !dir) continue
      planos.push({ location: pontoDeIfc(loc, ref), direction: direccaoDeIfc(dir, ref) })
    }

    return {
      camera: lerCamara(raiz, ref),
      clipping_planes: planos,
      selected_global_ids: guids(filho(comp, 'Selection')),
      hidden_global_ids: porOmissao ? excepcoes : [],
      visible_global_ids: porOmissao ? [] : excepcoes,
      default_visibility: porOmissao,
    }
  } catch {
    return null
  }
}

/** A cor de um `Color` do BCF em `#rrggbb`. O BCF escreve 6 ou 8
 *  dígitos hexadecimais; com 8, os dois primeiros são a opacidade (ARGB),
 *  que aqui não se usa. */
export function corDoBcf(valor: string | null): string | null {
  if (!valor) return null
  const v = valor.trim().replace(/^#/, '')
  if (!/^[0-9a-fA-F]{6}([0-9a-fA-F]{2})?$/.test(v)) return null
  const rgb = v.length === 8 ? v.slice(2) : v
  return `#${rgb.toLowerCase()}`
}

/**
 * As cores que o `.bcfv` dá a cada componente (`Components > Coloring`).
 *
 * Na 2.1 os `Component` estão dentro de cada `Color`; na 3.0 dentro de
 * `Color > Components`. Lêem-se as duas formas. Um GlobalId com duas
 * cores fica com a primeira. Nunca lança: um XML ilegível dá um mapa
 * vazio.
 */
export function lerCores(xml: string): Map<string, string> {
  const out = new Map<string, string>()
  try {
    const doc = new DOMParser().parseFromString(xml, 'application/xml')
    if (doc.getElementsByTagName('parsererror')[0]) return out
    const coloring = doc.getElementsByTagName('Coloring')[0]
    if (!coloring) return out
    for (const cor of Array.from(coloring.getElementsByTagName('Color'))) {
      const hex = corDoBcf(cor.getAttribute('Color'))
      if (!hex) continue
      for (const c of Array.from(cor.getElementsByTagName('Component'))) {
        const g = c.getAttribute('IfcGuid')
        if (g && !out.has(g)) out.set(g, hex)
      }
    }
  } catch {
    /* sem cores do ficheiro: usa-se a paleta */
  }
  return out
}
